import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { getUrl, uploadData } from "aws-amplify/storage";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/AdminLayout.jsx";

const client = generateClient();

const getStoragePath = (value) => {
  if (!value.startsWith("http")) return value;
  return decodeURIComponent(new URL(value).pathname.slice(1));
};

const withCoverUrls = (items) =>
  Promise.all(
    items.map(async (libro) => {
      const { url } = await getUrl({ path: getStoragePath(libro.image) });
      return { ...libro, imageUrl: url.href };
    }),
  );

export default function LibrosAdmin() {
  const [libros, setLibros] = useState([]);
  const [newLibro, setNewLibro] = useState({
    name: "",
    image: "",
    pagesFolder: "",
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewPages, setPreviewPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFolderValid, setIsFolderValid] = useState(true);
  const [coverFile, setCoverFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLibro, setEditingLibro] = useState(null);

  useEffect(() => {
    const fetchLibros = async () => {
      try {
        const { data: items } = await client.models.Libro.list({
          authMode: "userPool",
        });
        console.log("Libros cargados:", items);
        setLibros(await withCoverUrls(items));
      } catch (error) {
        console.error("Error al cargar los libros:", error);
        alert("Hubo un error al cargar los libros.");
      }
    };
    fetchLibros();
  }, []);

  const validateFolderName = () => {
    if (!newLibro.name.trim()) return;
    const existingLibro = libros.find(
      (libro) => libro.pagesFolder === newLibro.name,
    );
    setIsFolderValid(!existingLibro);
    if (existingLibro) {
      alert("El nombre del libro ya existe. Por favor, elija otro.");
    }
  };

  const handleFileUpload = (files) => {
    const fileArray = Array.from(files);
    const fileURLs = fileArray.map((file) => URL.createObjectURL(file));
    setSelectedImages((prev) => [...prev, ...fileArray]);
    setPreviewPages((prev) => [...prev, ...fileURLs]);
  };

  const handleUploadPortada = (file) => {
    const localCoverURL = URL.createObjectURL(file);
    setCoverFile(file);
    setNewLibro({ ...newLibro, image: localCoverURL });
  };

  const handleSaveLibro = async () => {
    setLoading(true);
    try {
      const folderPath = `${newLibro.name}`;
      let portadaPath = editingLibro ? getStoragePath(editingLibro.image) : "";

      if (coverFile) {
        const uploadPath = `libros/portadas/${newLibro.name}/${coverFile.name}`;
        const portadaResult = await uploadData({
          path: uploadPath,
          data: coverFile,
        }).result;
        if (portadaResult.path) {
          portadaPath = portadaResult.path;
        } else {
          throw new Error("Error al subir la portada.");
        }
      }

      if (editingLibro) {
        // Modo edición
        await client.models.Libro.update(
          {
            id: editingLibro.id,
            name: newLibro.name,
            image: portadaPath,
            pagesFolder: newLibro.name,
          },
          {
            authMode: "userPool",
          },
        );
        alert("Libro actualizado con éxito.");
      } else {
        // Modo creación
        const uploads = selectedImages.map(async (file) => {
          try {
            const result = await uploadData({
              path: `libros/${folderPath}/${file.name}`,
              data: file,
            }).result;
            if (!result.path) throw new Error(`Error al subir: ${file.name}`);
            return result.path;
          } catch (err) {
            console.error(`Error al subir ${file.name}:`, err);
            return null;
          }
        });

        const uploadedImages = (await Promise.all(uploads)).filter(Boolean);
        if (uploadedImages.length === 0) {
          throw new Error("No se pudo subir ninguna página.");
        }

        await client.models.Libro.create(
          {
            ...newLibro,
            image: portadaPath,
            pagesFolder: newLibro.name,
          },
          {
            authMode: "userPool",
          },
        );

        alert("Libro guardado con éxito.");
      }

      setNewLibro({ name: "", image: "", pagesFolder: "" });
      setSelectedImages([]);
      setPreviewPages([]);
      setCoverFile(null);
      setEditingLibro(null);
      setShowForm(false);

      const { data: items } = await client.models.Libro.list({
        authMode: "userPool",
      });
      setLibros(await withCoverUrls(items));
    } catch (error) {
      console.error("Error guardando el libro:", error);
      alert("Hubo un error al guardar el libro.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLibro = async (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este libro?")) {
      try {
        console.log("Eliminando libro con ID:", id);
        const result = await client.models.Libro.delete(
          { id },
          {
            authMode: "userPool",
          },
        );
        console.log("Resultado de la eliminación:", result);
        alert("Libro eliminado con éxito.");
        setLibros(libros.filter((libro) => libro.id !== id));
      } catch (error) {
        console.error("Error eliminando el libro:", error);
        alert("Hubo un error al eliminar el libro.");
      }
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="flex min-h-screen bg-gray-100 p-4">
          <div className="w-full rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">
              Administrar Libros
            </h2>

            <button
              className="mb-6 rounded-lg bg-blue-500 px-6 py-2 font-medium text-white shadow-md hover:bg-blue-600"
              onClick={() => {
                setShowForm(!showForm);
                setEditingLibro(null);
                setNewLibro({ name: "", image: "", pagesFolder: "" });
                setCoverFile(null);
                setSelectedImages([]);
              }}
            >
              {showForm ? "Cancelar" : "Agregar Nuevo Libro"}
            </button>

            {showForm && (
              <div className="mb-6 border-t pt-4">
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Nombre del Libro
                  </label>
                  <input
                    type="text"
                    className="block w-full rounded-lg border px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    value={newLibro.name}
                    onChange={(e) =>
                      setNewLibro({ ...newLibro, name: e.target.value })
                    }
                    onBlur={validateFolderName}
                  />
                </div>
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Portada
                  </label>
                  <input
                    type="file"
                    className="block w-full cursor-pointer rounded-lg border px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                    onChange={(e) => handleUploadPortada(e.target.files[0])}
                  />
                  {newLibro.image && (
                    <img
                      src={coverFile ? newLibro.image : editingLibro?.imageUrl}
                      alt="Portada"
                      className="mt-4 h-32 w-32 rounded-lg border object-cover"
                    />
                  )}
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Cargar Imágenes
                  </label>
                  <input
                    type="file"
                    multiple
                    className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    disabled={!newLibro.image || !isFolderValid}
                  />
                  <div className="mt-4 flex flex-wrap gap-2">
                    {previewPages.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`Uploaded ${index}`}
                        className="h-16 w-16 rounded-lg border border-gray-300 object-cover"
                      />
                    ))}
                  </div>
                </div>
                <button
                  className="rounded-lg bg-green-500 px-6 py-2 font-medium text-white shadow-md hover:bg-green-600 focus:outline-none"
                  onClick={handleSaveLibro}
                  disabled={loading || !newLibro.name.trim() || !coverFile}
                >
                  {loading
                    ? "Guardando..."
                    : editingLibro
                      ? "Actualizar Libro"
                      : "Guardar Libro"}
                </button>
              </div>
            )}

            <table className="w-full overflow-hidden rounded-lg border border-gray-200 shadow-lg">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                    Portada
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {libros.map((libro) => (
                  <tr key={libro.id} className="transition hover:bg-gray-100">
                    <td className="px-6 py-4">
                      <img
                        src={libro.imageUrl}
                        alt={libro.name}
                        className="h-20 w-20 rounded-lg border border-gray-300 shadow-sm"
                      />
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {libro.name}
                    </td>
                    <td className="flex gap-2 px-6 py-4">
                      <button
                        className="rounded-md bg-yellow-500 px-4 py-2 text-white shadow transition hover:bg-yellow-600"
                        onClick={() => {
                          setEditingLibro(libro);
                          setNewLibro({
                            name: libro.name,
                            image: libro.image,
                            pagesFolder: libro.pagesFolder,
                          });
                          setShowForm(true);
                        }}
                      >
                        Editar
                      </button>
                      <button
                        className="rounded-md bg-red-500 px-4 py-2 text-white shadow transition hover:bg-red-600"
                        onClick={() => handleDeleteLibro(libro.id)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AuthInterceptor>
    </Layout>
  );
}
